import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import {
  TeammateAvatarConfig,
  TeammateAvatarCropContext,
} from './teammate-avatar.config';
import {
  EYE_DEFINITIONS,
  EyeDefinition,
  SHAPE_DEFINITIONS,
} from './teammate-avatar.geometry';

gsap.registerPlugin(MorphSVGPlugin, CustomEase);

// Full-body bouncy spring — cubic-bezier(0.34, 1.56, 0.64, 1)
CustomEase.create('bouncySpring', 'M0,0 C0.34,1.56 0.64,1 1,1');
// Cropped quick ease-out — cubic-bezier(0.25, 0.46, 0.45, 0.94)
CustomEase.create('quickEaseOut', 'M0,0 C0.25,0.46 0.45,0.94 1,1');
/**
 * Loading loop — hang time at peak, snappy return
 * Up: Slower liftoff with a very long deceleration into the peak.
 * Down: Snappy drop that accelerates early but lands softly.
 */
CustomEase.create('organicFloatUp', 'M0,0 C0.35,0 0.2,1 1,1');
CustomEase.create('organicFloatDown', 'M0,0 C0.45,0 0.6,1 1,1');

const FINE_POINTER_QUERY = '(pointer: fine)';

const FULL_BODY_REGULAR_STATE = { x: 0, y: 12, rotation: 0 };

const SHADOW = {
  regularWidth: 72,
  hoverWidth: 56,
};

const HOVER_ANIMATION = {
  cropped: {
    duration: 0.6,
    ease: 'quickEaseOut' as gsap.EaseString,
  },
  fullBody: {
    duration: 1.2,
    ease: 'bouncySpring' as gsap.EaseString,
  },
  eyeMorph: {
    duration: 0.85,
    ease: 'bouncySpring' as gsap.EaseString,
  },
} as const;

const LOADING_LOOP_ANIMATION = {
  duration: {
    up: 0.92, // Increased for more "floaty" hang time
    down: 0.68, // Sharper return to baseline
    overlap: 0.12, // More overlap creates a smoother "arc" at the peak
  },
  ease: {
    up: 'organicFloatUp' as gsap.EaseString,
    down: 'organicFloatDown' as gsap.EaseString,
  },
} as const;

interface EyeElements {
  eyePath: SVGPathElement | undefined;
  leftEyePath: SVGPathElement | undefined;
  rightEyePath: SVGPathElement | undefined;
}

@Component({
  selector: 'squadbox-teammate-avatar',
  host: {
    class: 'block h-full w-full',
  },
  template: `
    <div
      class="relative h-full w-full overflow-hidden"
      [class.rounded-t-2xl]="!isCropped() && !transparentBackground()"
      [style.--teammate-avatar-base-color]="avatar().baseColor"
      [style.--teammate-avatar-dark-color]="avatar().darkColor"
      (pointerenter)="playHoverAnimation()"
      (pointerleave)="reverseHoverAnimation()"
    >
      @if (!isCropped()) {
        @if (!transparentBackground()) {
          <div
            class="absolute inset-0"
            style="background-color: var(--teammate-avatar-base-color)"
          ></div>

          <div
            class="absolute inset-0 bg-shades-white"
            style="opacity: 0.84"
          ></div>
        }

        <div class="pointer-events-none absolute inset-x-0 top-3 h-32">
          <div
            #shapeWrapper
            class="absolute left-1/2 top-0 size-24 -translate-x-1/2 overflow-visible"
          >
            <div class="absolute inset-0 flex items-center justify-center">
              <svg
                [attr.viewBox]="shapeDefinition().viewBox"
                [style.width.px]="shapeDefinition().width"
                [style.height.px]="shapeDefinition().height"
                [style.transform]="
                  'rotate(' + shapeDefinition().innerRotation + 'deg)'
                "
                overflow="visible"
              >
                <path
                  #shapePath
                  [attr.d]="shapeDefinition().regularPath"
                  fill="var(--teammate-avatar-base-color)"
                />
              </svg>
            </div>

            <div class="absolute inset-0 flex items-center justify-center">
              <svg
                [attr.viewBox]="eyeDefinition().viewBox"
                [style.width.px]="eyeDefinition().width"
                [style.height.px]="eyeDefinition().height"
                [style.transform]="
                  'translate(' +
                  eyeDefinition().offsetX +
                  'px, ' +
                  eyeDefinition().offsetY +
                  'px)'
                "
                overflow="visible"
              >
                @if (eyeDefinition().split) {
                  <path
                    #leftEyePath
                    [attr.d]="getInitialLeftEyePath()"
                    fill="var(--color-neutral-800)"
                  />
                  <path
                    #rightEyePath
                    [attr.d]="getInitialRightEyePath()"
                    fill="var(--color-neutral-800)"
                  />
                } @else {
                  <path
                    #eyePath
                    [attr.d]="getInitialSingleEyePath()"
                    fill="var(--color-neutral-800)"
                  />
                }
              </svg>
            </div>
          </div>

          <div
            #shadow
            class="absolute bottom-0 left-1/2 h-6 -translate-x-1/2 overflow-visible"
            [style.width.px]="SHADOW_REGULAR_WIDTH"
          >
            <div
              class="h-full w-full rounded-full"
              style="
                background-color: var(--teammate-avatar-dark-color);
                filter: blur(8px);
                opacity: 0.36;
              "
            ></div>
          </div>
        </div>
      } @else {
        <!-- Base color background layer -->
        @if (variant() === 'large') {
          <div
            class="absolute inset-0"
            style="background-color: var(--teammate-avatar-base-color)"
          ></div>
        } @else {
          @if (!active() && !(hovered() ?? isInternallyHovered())) {
            <div
              class="absolute inset-0"
              style="background-color: var(--teammate-avatar-base-color)"
            ></div>
          }
        }

        <!-- White overlay — always present -->
        <div
          class="absolute inset-0 bg-shades-white"
          style="opacity: 0.84"
        ></div>

        <!-- Shape content -->
        <div class="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            #shapeWrapper
            class="absolute top-0 left-0 overflow-visible"
            [style.width.px]="64 * croppedScale()"
            [style.height.px]="64 * croppedScale()"
          >
            <div class="absolute inset-0 flex items-center justify-center">
              <svg
                [attr.viewBox]="shapeDefinition().viewBox"
                [style.width.px]="
                  shapeDefinition().croppedContentSize * croppedScale()
                "
                [style.height.px]="
                  shapeDefinition().croppedContentSize * croppedScale()
                "
                [style.transform]="
                  'rotate(' + shapeDefinition().innerRotation + 'deg)'
                "
                overflow="visible"
              >
                <path
                  #shapePath
                  [attr.d]="shapeDefinition().regularPath"
                  fill="var(--teammate-avatar-base-color)"
                />
              </svg>
            </div>

            <div class="absolute inset-0 flex items-center justify-center">
              <svg
                [attr.viewBox]="eyeDefinition().viewBox"
                [style.width.px]="
                  eyeDefinition().width *
                  (shapeDefinition().croppedContentSize /
                    shapeDefinition().width) *
                  croppedScale()
                "
                [style.height.px]="
                  eyeDefinition().height *
                  (shapeDefinition().croppedContentSize /
                    shapeDefinition().width) *
                  croppedScale()
                "
                [style.transform]="
                  'translate(' +
                  shapeDefinition().croppedEyeOffsetX * croppedScale() +
                  'px, ' +
                  shapeDefinition().croppedEyeOffsetY * croppedScale() +
                  'px)'
                "
                overflow="visible"
              >
                @if (eyeDefinition().split) {
                  <path
                    #leftEyePath
                    [attr.d]="getInitialLeftEyePath()"
                    fill="var(--color-neutral-800)"
                  />
                  <path
                    #rightEyePath
                    [attr.d]="getInitialRightEyePath()"
                    fill="var(--color-neutral-800)"
                  />
                } @else {
                  <path
                    #eyePath
                    [attr.d]="getInitialSingleEyePath()"
                    fill="var(--color-neutral-800)"
                  />
                }
              </svg>
            </div>
          </div>
        </div>

        <!-- Large variant: persistent white border ring -->
        @if (variant() === 'large') {
          <div
            class="pointer-events-none absolute inset-0 border-4 border-shades-white"
          ></div>
        }
      }
    </div>
  `,
})
export class TeammateAvatarComponent implements OnDestroy {
  public readonly SHADOW_REGULAR_WIDTH = SHADOW.regularWidth;

  public readonly avatar = input.required<TeammateAvatarConfig>();
  public readonly variant = input<'card' | TeammateAvatarCropContext>('card');
  /**
   * When true (full-body `card` only), the tinted card backdrop layers are
   * dropped so the body + shadow float on whatever sits behind (e.g. the
   * Builder's tinted banner — Figma 24767-23840). Default keeps the card bg.
   */
  public readonly transparentBackground = input<boolean>(false);
  public readonly hovered = input<boolean | undefined>(undefined);
  public readonly active = input<boolean>(false);
  public readonly looping = input<boolean>(false);

  private readonly shapeWrapper =
    viewChild<ElementRef<HTMLDivElement>>('shapeWrapper');
  private readonly eyePath = viewChild<ElementRef<SVGPathElement>>('eyePath');
  private readonly leftEyePath =
    viewChild<ElementRef<SVGPathElement>>('leftEyePath');
  private readonly rightEyePath =
    viewChild<ElementRef<SVGPathElement>>('rightEyePath');
  private readonly shadow = viewChild<ElementRef<HTMLDivElement>>('shadow');

  private activeTransition: gsap.core.Timeline | null = null;
  private loopTimeline: gsap.core.Timeline | null = null;

  // GSAP's global rAF ticker must run OUTSIDE the Angular zone — otherwise every
  // animation frame churns zone stability during change detection and re-enters
  // ApplicationRef.tick() (NG0101). The animations mutate SVG/DOM directly with
  // no template binding on progress, so running them zoneless is side-effect-free.
  private readonly ngZone = inject(NgZone);

  /** Tracks hover state for internal pointer events (used by small/x-small background toggle) */
  public readonly isInternallyHovered = signal(false);

  public readonly shapeDefinition = computed(
    () => SHAPE_DEFINITIONS[this.avatar().shape],
  );

  public readonly eyeDefinition = computed(
    () => EYE_DEFINITIONS[this.avatar().eyes],
  );

  public readonly isCropped = computed(() => this.variant() !== 'card');

  private readonly croppedFrameSize = computed<number>(() => {
    switch (this.variant()) {
      case 'x-small':
        return 32;
      case 'large':
        return 64;
      default:
        return 48;
    }
  });

  /** Scale factor relative to the Small (48 px) baseline */
  public readonly croppedScale = computed(() => this.croppedFrameSize() / 48);

  public getInitialLeftEyePath(): string {
    const eyes = this.eyeDefinition();
    return eyes.split ? eyes.leftRegularPath : '';
  }

  public getInitialRightEyePath(): string {
    const eyes = this.eyeDefinition();
    return eyes.split ? eyes.rightRegularPath : '';
  }

  public getInitialSingleEyePath(): string {
    const eyes = this.eyeDefinition();
    return eyes.split ? '' : eyes.regularPath;
  }

  constructor() {
    // Signal reads stay inside the effect's reactive context (zone-independent);
    // the GSAP work runs outside the Angular zone (see `ngZone` note above).
    effect(() => {
      this.avatar();
      this.shapeDefinition();
      this.eyeDefinition();
      this.variant();

      const shapeWrapper = this.shapeWrapper()?.nativeElement;
      if (!shapeWrapper) return;

      if (this.isCropped()) {
        this.ngZone.runOutsideAngular(() =>
          this.initializeCroppedAnimation(shapeWrapper),
        );
        return;
      }

      const shadow = this.shadow()?.nativeElement;
      const eyes = this.eyeDefinition();
      const eyeElements = this.getEyeElements();

      if (!shadow || !this.hasRequiredEyeElements(eyes, eyeElements)) {
        return;
      }

      this.ngZone.runOutsideAngular(() =>
        this.initializeFullBodyAnimation(shapeWrapper, shadow, eyeElements),
      );
    });

    effect(() => {
      const hovered = this.hovered();
      if (hovered === undefined) return;
      this.isInternallyHovered.set(hovered);
      this.ngZone.runOutsideAngular(() => this.animateToState(hovered));
    });

    effect(() => {
      const shouldLoop = this.looping();
      this.avatar();
      this.shapeDefinition();

      if (shouldLoop) {
        this.ngZone.runOutsideAngular(() =>
          requestAnimationFrame(() => this.startLoopAnimation()),
        );
      } else {
        this.ngZone.runOutsideAngular(() => this.stopLoopAnimation());
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupAllAnimations();
  }

  public playHoverAnimation(): void {
    if (this.hovered() !== undefined || this.looping()) return;
    if (this.isCropped() && !window.matchMedia(FINE_POINTER_QUERY).matches)
      return;
    this.isInternallyHovered.set(true);
    this.ngZone.runOutsideAngular(() => this.animateToState(true));
  }

  public reverseHoverAnimation(): void {
    if (this.hovered() !== undefined || this.looping()) return;
    if (this.isCropped() && !window.matchMedia(FINE_POINTER_QUERY).matches)
      return;
    this.isInternallyHovered.set(false);
    this.ngZone.runOutsideAngular(() => this.animateToState(false));
  }

  private initializeFullBodyAnimation(
    shapeWrapper: HTMLDivElement,
    shadow: HTMLDivElement,
    eyeElements: EyeElements,
  ): void {
    this.cleanupHoverAnimation();

    gsap.set(shapeWrapper, {
      x: FULL_BODY_REGULAR_STATE.x,
      y: FULL_BODY_REGULAR_STATE.y,
      rotation: FULL_BODY_REGULAR_STATE.rotation,
      transformOrigin: '50% 50%',
    });
    gsap.set(shadow, { width: SHADOW.regularWidth });
    this.setInitialEyePaths(this.eyeDefinition(), eyeElements);
  }

  private initializeCroppedAnimation(shapeWrapper: HTMLDivElement): void {
    this.cleanupHoverAnimation();

    const scale = this.croppedScale();
    const state = this.shapeDefinition().croppedDefaultState;

    gsap.set(shapeWrapper, {
      x: state.x * scale,
      y: state.y * scale,
      rotation: state.rotation,
      transformOrigin: '50% 50%',
    });

    const eyes = this.eyeDefinition();
    const eyeElements = this.getEyeElements();
    if (this.hasRequiredEyeElements(eyes, eyeElements)) {
      this.setInitialEyePaths(eyes, eyeElements);
    }
  }

  private animateToState(isHovered: boolean): void {
    const shapeWrapper = this.shapeWrapper()?.nativeElement;
    if (!shapeWrapper) return;

    this.activeTransition?.kill();
    this.activeTransition = gsap.timeline();

    if (this.isCropped()) {
      if (!window.matchMedia(FINE_POINTER_QUERY).matches) return;

      const scale = this.croppedScale();
      const shape = this.shapeDefinition();
      const state = isHovered
        ? shape.croppedHoverState
        : shape.croppedDefaultState;

      this.activeTransition.to(shapeWrapper, {
        x: state.x * scale,
        y: state.y * scale,
        rotation: state.rotation,
        duration: HOVER_ANIMATION.cropped.duration,
        ease: HOVER_ANIMATION.cropped.ease,
      });

      const croppedEyes = this.eyeDefinition();
      const croppedEyeElements = this.getEyeElements();
      if (this.hasRequiredEyeElements(croppedEyes, croppedEyeElements)) {
        this.animateEyePaths(croppedEyes, croppedEyeElements, isHovered);
      }
      return;
    }

    // Full-body animation
    const shadow = this.shadow()?.nativeElement;
    const eyes = this.eyeDefinition();
    const eyeElements = this.getEyeElements();

    if (!shadow || !this.hasRequiredEyeElements(eyes, eyeElements)) return;

    const shape = this.shapeDefinition();
    const targetX = isHovered ? shape.hoverState.x : FULL_BODY_REGULAR_STATE.x;
    const targetY = isHovered ? shape.hoverState.y : FULL_BODY_REGULAR_STATE.y;
    const targetRotation = isHovered
      ? shape.hoverState.rotation
      : FULL_BODY_REGULAR_STATE.rotation;
    const targetShadowWidth = isHovered
      ? SHADOW.hoverWidth
      : SHADOW.regularWidth;

    this.activeTransition
      .to(
        shapeWrapper,
        {
          x: targetX,
          y: targetY,
          rotation: targetRotation,
          duration: HOVER_ANIMATION.fullBody.duration,
          ease: HOVER_ANIMATION.fullBody.ease,
        },
        0,
      )
      .to(
        shadow,
        {
          width: targetShadowWidth,
          duration: HOVER_ANIMATION.fullBody.duration,
          ease: HOVER_ANIMATION.fullBody.ease,
        },
        0,
      );

    this.animateEyePaths(eyes, eyeElements, isHovered);
  }

  private startLoopAnimation(): void {
    const shapeWrapper = this.shapeWrapper()?.nativeElement;
    const shadow = this.shadow()?.nativeElement;

    if (!shapeWrapper || !shadow) return;

    this.stopLoopAnimation();
    this.cleanupHoverAnimation();

    const shape = this.shapeDefinition();

    gsap.set(shapeWrapper, {
      x: FULL_BODY_REGULAR_STATE.x,
      y: FULL_BODY_REGULAR_STATE.y,
      rotation: FULL_BODY_REGULAR_STATE.rotation,
      transformOrigin: '50% 50%',
    });
    gsap.set(shadow, { width: SHADOW.regularWidth });

    const hoverStart = 0;
    const returnStart =
      LOADING_LOOP_ANIMATION.duration.up -
      LOADING_LOOP_ANIMATION.duration.overlap;

    this.loopTimeline = gsap.timeline({ repeat: -1 });

    this.loopTimeline
      .to(
        shadow,
        {
          width: SHADOW.hoverWidth,
          duration: LOADING_LOOP_ANIMATION.duration.up,
          ease: LOADING_LOOP_ANIMATION.ease.up,
        },
        hoverStart,
      )
      .to(
        shapeWrapper,
        {
          x: shape.hoverState.x,
          y: shape.hoverState.y,
          rotation: shape.hoverState.rotation,
          duration: LOADING_LOOP_ANIMATION.duration.up,
          ease: LOADING_LOOP_ANIMATION.ease.up,
        },
        hoverStart,
      )
      .to(
        shadow,
        {
          width: SHADOW.regularWidth,
          duration: LOADING_LOOP_ANIMATION.duration.down,
          ease: LOADING_LOOP_ANIMATION.ease.down,
        },
        returnStart,
      )
      .to(
        shapeWrapper,
        {
          x: FULL_BODY_REGULAR_STATE.x,
          y: FULL_BODY_REGULAR_STATE.y,
          rotation: FULL_BODY_REGULAR_STATE.rotation,
          duration: LOADING_LOOP_ANIMATION.duration.down,
          ease: LOADING_LOOP_ANIMATION.ease.down,
        },
        returnStart,
      );
  }

  private stopLoopAnimation(): void {
    if (this.loopTimeline) {
      this.loopTimeline.kill();
      this.loopTimeline = null;
    }
  }

  private getEyeElements(): EyeElements {
    return {
      eyePath: this.eyePath()?.nativeElement,
      leftEyePath: this.leftEyePath()?.nativeElement,
      rightEyePath: this.rightEyePath()?.nativeElement,
    };
  }

  private hasRequiredEyeElements(
    eyes: EyeDefinition,
    eyeElements: EyeElements,
  ): boolean {
    return eyes.split
      ? !!eyeElements.leftEyePath && !!eyeElements.rightEyePath
      : !!eyeElements.eyePath;
  }

  private setInitialEyePaths(
    eyes: EyeDefinition,
    eyeElements: EyeElements,
  ): void {
    if (eyes.split) {
      if (!eyeElements.leftEyePath || !eyeElements.rightEyePath) return;

      gsap.set(eyeElements.leftEyePath, {
        attr: { d: eyes.leftRegularPath },
      });
      gsap.set(eyeElements.rightEyePath, {
        attr: { d: eyes.rightRegularPath },
      });
      return;
    }

    if (!eyeElements.eyePath) return;

    gsap.set(eyeElements.eyePath, { attr: { d: eyes.regularPath } });
  }

  private animateEyePaths(
    eyes: EyeDefinition,
    eyeElements: EyeElements,
    isHovered: boolean,
  ): void {
    if (!this.activeTransition) return;

    const { duration, ease } = HOVER_ANIMATION.eyeMorph;

    if (eyes.split) {
      if (!eyeElements.leftEyePath || !eyeElements.rightEyePath) return;

      this.activeTransition
        .to(
          eyeElements.leftEyePath,
          {
            morphSVG: isHovered ? eyes.leftHoverPath : eyes.leftRegularPath,
            duration,
            ease,
          },
          0,
        )
        .to(
          eyeElements.rightEyePath,
          {
            morphSVG: isHovered ? eyes.rightHoverPath : eyes.rightRegularPath,
            duration,
            ease,
          },
          0,
        );
      return;
    }

    if (!eyeElements.eyePath) return;

    this.activeTransition.to(
      eyeElements.eyePath,
      {
        morphSVG: isHovered ? eyes.hoverPath : eyes.regularPath,
        duration,
        ease,
      },
      0,
    );
  }

  private cleanupHoverAnimation(): void {
    if (this.activeTransition) {
      this.activeTransition.kill();
      this.activeTransition = null;
    }
  }

  private cleanupAllAnimations(): void {
    this.cleanupHoverAnimation();
    this.stopLoopAnimation();
  }
}
